import OliverText from "@builder/components/general/OliverText";
import {
    deleteActionBuilder,
    duplicateActionBuilder,
    setSpecialLayers,
    toggleAutoAction,
    updateBuildActions,
    updateNodeActions
} from "@builder/store/slices/BuilderSlice/builderSlice";
import { clearActionsEditor, setActionsEditor, setCurrent } from "@builder/store/slices/actionBuilderSlice";
import { closePopup, setCurrentPopup } from "@builder/store/slices/popupSlice";
import { useAppDispatch, useAppSelector } from "@builder/store/store";
import { ActionBuilderType, reconstructActionEditor } from "@builder/utils/actionBuilderHelpers";
import { ActionBuilder, SpecialLayers } from "@common/build/types";
import { Divider } from "@mui/material";
import { Fragment } from "react";
// import TemplateActionSelectors from "./TemplateActionSelectors";
import AltSelectInput from "@builder/components/general/AltSelectInput";
import Button from "@builder/components/general/Button";
import YoutubeButton from "@builder/components/general/Button/YoutubeButton";
import { useTranslation } from "react-i18next";
import ActionIconList from "./ActionIconList";
import Toggle from "@builder/components/general/Toggle";
// import { SidebarOption } from "../../PopupSidebar";

const ActionSelectors = ({ searchInput }: { searchInput?: string }) => {
    const dispatch = useAppDispatch();
    const actionArray = useAppSelector(state => state.builder.build.actions);
    const actionFromNode = useAppSelector(state => state.popup.actionFromNode);
    const actions = useAppSelector(state => state.builder.build.actionBuilder) || {};
    const checkoutComplete = useAppSelector(state => state.builder.build.specialLayers?.checkoutComplete);
    const isAuto = useAppSelector(state => state.popup.isAutomation);
    const autoActions = Object.values(actions).filter(action => action.isAuto);
    const manualActions = Object.values(actions).filter(action => !action.isAuto);
    const actionPath = useAppSelector(state => state.popup.actionPath);
    const { t } = useTranslation();

    const handleAddAction = (actionBuilder: ActionBuilder) => {
        if (actionFromNode) {
            dispatch(updateNodeActions({ actionId: actionBuilder.id, actionPath }));
        } else {
            dispatch(updateBuildActions(actionBuilder.id));
        }
        dispatch(closePopup());
    };

    const handleEdit = (actionBuilder: ActionBuilder) => {
        const actionsEditor = reconstructActionEditor(actionBuilder);
        dispatch(setCurrentPopup("actionBuilder"));
        dispatch(setActionsEditor(actionsEditor));
        dispatch(setCurrent({ id: actionsEditor[0].id || "", tab: ActionBuilderType.START }));
    };

    const handleCreateFromScratch = () => {
        dispatch(clearActionsEditor());
        dispatch(setCurrentPopup("actionBuilder"));
    };

    const handleCheckoutCompleteLayer = (option: any) => {
        const updatedCheckoutComplete = { ...checkoutComplete, action: option.id, name: option.name };
        dispatch(setSpecialLayers({ checkoutComplete: updatedCheckoutComplete } as SpecialLayers));
    };

    const filterByName = (action: ActionBuilder) => {
        if (!searchInput) return true;
        return action?.name.toLowerCase().includes(searchInput.toLowerCase());
    };

    return (
        <div className="selectors-wrapper">
            {actions && Object.keys(actions).length > 0 ? (
                <>
                    <div className="action-creator-wrapper checkout-complete">
                        <div className="action-creator">
                            <OliverText text="Checkout completed" weight="medium-heavy" size="l" translatePrefix="popup.actionBuilder." />
                            <OliverText
                                text="Set an action you want to run when a checkout is completed."
                                size="xs"
                                weight="light"
                                translatePrefix="popup.actionBuilder."
                            />
                        </div>
                        <AltSelectInput
                            className="page-selector"
                            selected={manualActions.find(action => action.id === checkoutComplete?.action)?.name}
                            options={manualActions && manualActions.length > 0 ? manualActions : [{ id: "", name: "", type: "" }]}
                            getLabel={(option: any) => option.name}
                            callback={handleCheckoutCompleteLayer}
                        />
                    </div>

                    <div className="actions-wrapper">
                        <div className="actions-items-wrapper">
                            <div className="apart">
                                <OliverText
                                    text={isAuto ? "Your automatically triggered actions" : "Your manually triggered actions"}
                                    weight="medium-heavy"
                                    translatePrefix="popup.actionBuilder."
                                />
                                <Button
                                    text="Create new action"
                                    buttonStyle="primary"
                                    size="small"
                                    onClick={handleCreateFromScratch}
                                    style={{ padding: "8px 16px" }}
                                />
                            </div>
                            <div className="divider" />

                            {!isAuto ? (
                                <>
                                    {manualActions && manualActions.length > 0 ? (
                                        manualActions.filter(filterByName).map((manual, index) => (
                                            <Fragment key={`manual-item-${manual.id}`}>
                                                <div className="action-item-wrapper">
                                                    <div className="action-item-info">
                                                        <div>
                                                            <OliverText text={manual.name} size="s" weight="medium-heavy" className="elipsis" />
                                                        </div>
                                                        <ActionIconList actions={manual.actions} />
                                                    </div>
                                                    <div className="action-button-wrapper">
                                                        <button className="square-button" onClick={() => handleEdit(manual)}>
                                                            <img src="./edit-conditions.svg" alt="" />
                                                        </button>
                                                        <button className="square-button" onClick={() => dispatch(duplicateActionBuilder(manual.id))}>
                                                            <img className="pointer-style" src={"./action-duplicate.svg"} />
                                                        </button>
                                                        <button className="square-button" onClick={() => dispatch(deleteActionBuilder(manual.id))}>
                                                            <img className="pointer-style" src={"./action-delete.svg"} />
                                                        </button>
                                                        <div style={{ borderLeft: "1px solid #D4DEE5", width: "1px", height: "12px" }} />
                                                        <button
                                                            className="link-action-button"
                                                            onClick={() => handleAddAction(manual)}
                                                            disabled={!actionFromNode}
                                                        >
                                                            {t("generalComponents.button.Link")}

                                                            <img style={{ width: "24px", height: "24px" }} src={"./action-link.svg"} />
                                                        </button>
                                                    </div>
                                                </div>
                                                {index < manualActions.length - 1 && <Divider style={{ borderColor: "#F0F2F4" }} />}
                                            </Fragment>
                                        ))
                                    ) : (
                                        <OliverText text="No actions yet" size="s" color="base-grey" />
                                    )}
                                </>
                            ) : (
                                <>
                                    {isAuto && autoActions && autoActions.length > 0 ? (
                                        <div className="actions-items-wrapper">
                                            {autoActions.filter(filterByName).map((auto, index) => (
                                                <Fragment key={`auto-item-${auto.id}`}>
                                                    <div className="action-item-wrapper">
                                                        <div className="action-item-info">
                                                            <div className="action-item-title-wrapper">
                                                                <OliverText
                                                                    text="Auto:"
                                                                    size="s"
                                                                    weight="medium-heavy"
                                                                    translatePrefix="popup.actionBuilder."
                                                                />
                                                                <OliverText text={auto.name} size="s" weight="medium" className="elipsis" />
                                                            </div>
                                                            <ActionIconList actions={auto.actions} />
                                                        </div>
                                                        <div className="action-button-wrapper">
                                                            <button className="square-button" onClick={() => handleEdit(auto)}>
                                                                <img src="./edit-conditions.svg" alt="" />
                                                            </button>
                                                            <button
                                                                className="square-button"
                                                                onClick={() => dispatch(duplicateActionBuilder(auto.id))}
                                                            >
                                                                <img className="pointer-style" src={"./action-duplicate.svg"} />
                                                            </button>
                                                            <button onClick={() => dispatch(deleteActionBuilder(auto.id))} className="square-button">
                                                                <img className="pointer-style" src={"./action-delete.svg"} />
                                                            </button>
                                                            <div style={{ borderLeft: "1px solid #D4DEE5", width: "1px", height: "12px" }} />
                                                            <Toggle
                                                                checked={(actionArray || []).includes(auto.id)}
                                                                callback={() => dispatch(toggleAutoAction(auto.id))}
                                                            />
                                                        </div>
                                                    </div>
                                                    {index < autoActions.length - 1 && <Divider style={{ borderColor: "#F0F2F4" }} />}
                                                </Fragment>
                                            ))}
                                        </div>
                                    ) : (
                                        <OliverText text="No actions yet" size="s" color="base-grey" />
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                </>
            ) : (
                <>
                    <div className="action-creator-wrapper">
                        <div className="action-creator">
                            <OliverText
                                text="First action huh?"
                                weight="medium-heavy"
                                style={{ fontSize: "var(--h3-font-size)" }}
                                translatePrefix="popup.actionBuilder."
                            />
                            <OliverText
                                text="Actions bring your build to life. Looks like you haven’t created any actions in this build yet. Let’s get you started!"
                                size="m"
                                weight="light"
                                translatePrefix="popup.actionBuilder."
                            />
                            <div className="action-button-wrapper">
                                <button className="action-button" onClick={handleCreateFromScratch}>
                                    {t("generalComponents.button.Create new action")}
                                </button>
                                <YoutubeButton link={import.meta.env.VITE_YOUTUBE_TUTORIAL_ACTIONS || "https://www.youtube.com/@FinalPOS"} />
                            </div>
                        </div>
                        <img src={"./action-start-image.svg"} />
                    </div>
                    {/* <div className="action-popular-templates">
                        <TemplateActionSelectors isPopular />
                    </div> */}
                </>
            )}
        </div>
    );
};

export default ActionSelectors;
